/**
 * The admin shell: sign in, enter a tenant, then the views.
 *
 * ── The information architecture, 2026-09-15 ─────────────────────────────
 *
 * Eight destinations in a rail, organised by what an operator does. The first
 * shell reproduced the predecessor's four menus and nineteen items faithfully
 * ("a recreation first"), and the result was a rail where sixty per cent of
 * the entries were reports over the same devices. Reports are now tabs on the
 * thing they report on: Map is a tab of Sites, Recordings and Pulls are tabs
 * of Footage, and the seven Admin screens are tabs of Settings.
 *
 * Nothing was cut. Every old `#/users`-shaped link still resolves — see the
 * aliases in `route.ts` — and the two unported destinations are still listed,
 * dimmed, because hiding one would make the rebuild look further along than
 * it is.
 */

import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import {
  Activity,
  Bell,
  Building,
  Check,
  ChevronsUpDown,
  Film,
  Gauge,
  LayoutGrid,
  LogOut,
  MapPin,
  Moon,
  Plus,
  Search,
  Settings,
  Signal,
  Sun,
  Zap,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  ApiError,
  api,
  type Fleet,
  type Me,
  type MfaStatus,
  type TenantOption,
} from './api.ts';
import { ActivityView } from './Activity.tsx';
import { AcceptInvite, PlatformTenants } from './Invites.tsx';
import { MailIdentityView, ReportScheduleView } from './MailIdentity.tsx';
import { PortalBrandingView } from './PortalBranding.tsx';
import { PlatformRecorders } from './Recorders.tsx';
import { MfaEnrol, TenantMfaPolicy } from './Mfa.tsx';
import { mfaRefusalStep } from './tenant-entry.ts';
import { applyConsoleBrand, brandBlock, BRANDING_CHANGED, consoleTitle, type ConsoleBranding } from './console-brand.ts';
import { ServiceStatus } from './ServiceStatus.tsx';
import { HostsView } from './Hosts.tsx';
import { RemoteAccessView } from './RemoteAccess.tsx';
import { AlertsView } from './Alerts.tsx';
import { CredentialsEditor } from './Credentials.tsx';
import { NewAsset } from './NewAsset.tsx';
import { SitesEditor } from './Sites.tsx';
import { AssetDetailPanel } from './AssetDetail.tsx';
import { ChannelsView } from './Channels.tsx';
import { FleetView } from './Fleet.tsx';
import { MapView } from './Map.tsx';
import { RoutingView } from './Routing.tsx';
import { UsersView } from './Users.tsx';
import { SimView } from './Sim.tsx';
import { PullsView } from './Pulls.tsx';
import { SettingsView } from './Settings.tsx';
import { UptimeView } from './Uptime.tsx';
import { UptimeMatrixView, UptimeReportView } from './UptimeReport.tsx';
import {
  Banner,
  Button,
  CommandPalette,
  Empty,
  Field,
  Loading,
  Section,
  Tabs,
  Toast,
  TooltipProvider,
  type PaletteItem,
} from './components/index.ts';
import { Recordings } from './Recordings.tsx';
import { GO_KEYS, readChord, type ChordState } from './shortcuts.ts';
import {
  SUB_VIEWS,
  formatHash,
  inviteTokenFromHash,
  malformedAssetLink,
  parseHash,
  routeTo,
  unknownViewLink,
  type DeepLinkFailure,
  type Route,
  type ViewKey,
} from './route.ts';
import { belongsInPortal, PORTAL_PATH } from './portal-redirect.ts';

interface NavItem {
  view: ViewKey;
  label: string;
  icon: React.ReactNode;
  /** False when the backend cannot serve it yet. Stated, not hidden. */
  ready: boolean;
}

/**
 * The rail. Order is by how often an operator goes there.
 *
 * 🔴 VPN, Provision Router and Import are NOT cut (owner, 2026-08-19); they
 * wait on the entitlement gate, because an item here with no gate is a screen
 * every tenant can reach. Scope for whoever picks it up is in
 * docs/PLAN-2026-09-15.md §3C and the VPN design doc.
 *
 * Customers is redundant: it was the predecessor's way of grouping sites
 * under an owner, which is what `tenants` now models.
 */
const NAV: NavItem[] = [
  { view: 'fleet', label: 'Fleet', icon: <LayoutGrid size={16} />, ready: true },
  { view: 'alerts', label: 'Alerts', icon: <Bell size={16} />, ready: true },
  { view: 'sites', label: 'Sites', icon: <MapPin size={16} />, ready: true },
  { view: 'uptime', label: 'Uptime', icon: <Gauge size={16} />, ready: true },
  { view: 'footage', label: 'Footage', icon: <Film size={16} />, ready: true },
  { view: 'sim', label: 'SIM', icon: <Signal size={16} />, ready: true },
  { view: 'activity', label: 'Activity', icon: <Activity size={16} />, ready: true },
  { view: 'power', label: 'Power', icon: <Zap size={16} />, ready: false },
  { view: 'settings', label: 'Settings', icon: <Settings size={16} />, ready: true },
];

/** The title in the topbar. Every destination names itself, once. */
const VIEW_TITLES: Record<ViewKey, string> = Object.fromEntries(
  NAV.map((item) => [item.view, item.label]),
) as Record<ViewKey, string>;

/** What each sub-view is called on its tab. */
const SUB_LABELS: Record<string, string> = {
  devices: 'Devices',
  assets: 'Assets',
  report: 'Report',
  matrix: 'Matrix',
  sites: 'By site',
  list: 'List',
  map: 'Map',
  recordings: 'Recordings',
  pulls: 'Pulled footage',
  vision: 'Vision',
  solar: 'Solar',
  triage: 'MPPT triage',
  users: 'Users',
  channels: 'Channels',
  routing: 'Alert routing',
  mail: 'Mail',
  portal: 'Client portal',
  credentials: 'Credentials',
  security: 'Security',
  hosts: 'Hosts',
  remote: 'Remote access',
  coverage: 'Check coverage',
  platform: 'Platform',
};

/** Sub-views the backend cannot serve yet. Listed, dimmed, honest. */
const PENDING_SUBS = new Set(['vision', 'solar', 'triage']);

/**
 * Theme, defaulting to DARK.
 *
 * This is a monitoring console, and status colour carries far more signal
 * against a dark ground than against white. `theme.css` defines dark on bare
 * `:root` for the same reason, so a first paint before this effect runs is
 * already correct rather than a white flash that then corrects itself.
 */
function useTheme(): [string, () => void] {
  const [theme, setTheme] = useState(() => localStorage.getItem('cosmon-theme') ?? 'dark');
  useEffect(() => {
    document.documentElement.dataset['theme'] = theme;
    localStorage.setItem('cosmon-theme', theme);
  }, [theme]);
  return [theme, (): void => setTheme((t) => (t === 'dark' ? 'light' : 'dark'))];
}

/**
 * The route, in the address bar.
 *
 * ── Why `location.hash =` and not `history.pushState` ────────────────────
 *
 * Assigning the hash pushes a history entry AND fires `hashchange`, so back and
 * forward are the browser's rather than ours: one listener, one state, and no
 * second code path for a restored entry to fall out of sync on. `pushState`
 * fires nothing, so every navigation would have to update the state itself and
 * `popstate` would have to update it again — two writers for one value, which is
 * how a Back button ends up showing the previous screen's URL over the current
 * screen's content.
 *
 * The initial read is in the `useState` initialiser, not an effect, because a
 * deep link has to be honoured by the FIRST paint. An effect would render the
 * fleet grid and then replace it, which for a link out of an alert email is a
 * flash of the wrong camera.
 */
function useHashRoute(): [Route, DeepLinkFailure | null, (next: Route) => void] {
  const [parsed, setParsed] = useState(() => parseHash(window.location.hash));

  useEffect(() => {
    const onChange = (): void => setParsed(parseHash(window.location.hash));
    window.addEventListener('hashchange', onChange);
    return (): void => window.removeEventListener('hashchange', onChange);
  }, []);

  const navigate = useCallback((next: Route): void => {
    const hash = formatHash(next);
    /*
     * Assigning an unchanged hash fires nothing, so the state would keep a
     * stale `unknownView` after a fallback landed the user on the fleet grid
     * and they then pressed Fleet. Setting it here as well makes the notice
     * dismissable by navigating, which is what pressing a nav button means.
     */
    if (window.location.hash === hash) setParsed(parseHash(hash));
    else window.location.hash = hash;
  }, []);

  /*
   * One notice at a time, most specific first. A hash can only be wrong in one
   * of these ways at once — an unknown view never carries a parsed asset — so
   * the order is about which fact is worth stating, not about precedence.
   */
  const failure =
    parsed.unknownView !== null
      ? unknownViewLink(parsed.unknownView)
      : parsed.malformedAssetId !== null
        ? malformedAssetLink(parsed.malformedAssetId)
        : null;

  return [parsed.route, failure, navigate];
}

export function App(): React.JSX.Element {
  const [theme, toggleTheme] = useTheme();
  const [me, setMe] = useState<Me | null>(null);
  const [loading, setLoading] = useState(true);
  const [route, routeFailure, navigate] = useHashRoute();

  const refreshMe = useCallback(async () => {
    try {
      setMe(await api.get<Me>('/api/me'));
    } catch {
      setMe(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refreshMe();
  }, [refreshMe]);

  /*
   * An invitation is opened by someone who may have no account yet, so it comes
   * before the session checks. `useHashRoute` re-renders on hashchange, which is
   * what lets leaving this page land on sign-in or the shell.
   */
  const inviteToken = inviteTokenFromHash(window.location.hash);
  if (inviteToken !== null) {
    return (
      <AcceptInvite
        token={inviteToken}
        theme={theme}
        onToggleTheme={toggleTheme}
        onDone={() => {
          window.location.hash = '#/';
          void refreshMe();
        }}
      />
    );
  }

  if (loading) return <Loading rows={3} cols={1} />;
  if (me === null) return <SignIn onSignedIn={refreshMe} theme={theme} onToggleTheme={toggleTheme} />;
  if (me.tenantId === null) {
    return (
      <TenantPicker
        onEntered={refreshMe}
        onSignOut={async () => {
          await api.post('/api/auth/logout');
          await refreshMe();
        }}
      />
    );
  }
  if (belongsInPortal(me.tenant?.role)) {
    // A client: the portal is theirs, and this console's API would refuse
    // them everywhere. Same sign-in, so nothing to enter again. Never from
    // /portal/ itself: where the console answers there (a dev server, a
    // misrouted proxy) that would reload forever.
    if (!window.location.pathname.startsWith(PORTAL_PATH)) {
      window.location.replace(PORTAL_PATH);
      return <Loading rows={1} cols={1} label="Opening your portal" />;
    }
    return (
      <Banner tone="info">This account is for the client portal, which is not served at this address.</Banner>
    );
  }

  return (
    <TooltipProvider>
      <Shell
        me={me}
        theme={theme}
        onToggleTheme={toggleTheme}
        route={route}
        routeFailure={routeFailure}
        navigate={navigate}
        refreshMe={refreshMe}
      />
    </TooltipProvider>
  );
}

function Shell({
  me,
  theme,
  onToggleTheme,
  route,
  routeFailure,
  navigate,
  refreshMe,
}: {
  me: Me;
  theme: string;
  onToggleTheme: () => void;
  route: Route;
  routeFailure: DeepLinkFailure | null;
  navigate: (next: Route) => void;
  refreshMe: () => Promise<void>;
}): React.JSX.Element {
  const view = route.view;
  const subs = SUB_VIEWS[view];

  const [fleet, setFleet] = useState<Fleet | null>(null);
  const [fleetError, setFleetError] = useState<string | null>(null);
  /*
   * Bumped by anything that changes what /api/fleet would return.
   *
   * A counter rather than a local edit of `fleet`: acknowledging an alert
   * changes a row the SERVER owns, and patching the copy in this component
   * would mean the screen showing what the browser hoped happened. Re-fetching
   * is a round trip nobody will notice on a screen that already loads the whole
   * fleet in one request.
   */
  const [generation, setGeneration] = useState(0);
  const reload = useCallback(() => setGeneration((n) => n + 1), []);

  useEffect(() => {
    let live = true;
    api
      .get<Fleet>('/api/fleet')
      .then((data) => live && setFleet(data))
      .catch((err: unknown) => live && setFleetError(err instanceof Error ? err.message : 'failed'));
    return (): void => {
      live = false;
    };
  }, [generation]);

  /*
   * The chosen tenant's look (2026-10-20). Fetched again whenever the tenant
   * changes or Settings › Client portal saves, and re-applied whenever the
   * theme changes, because a colour readable on one page is not on the other.
   * A failed fetch is CosMon's look, not an error: branding is decoration.
   */
  const [branding, setBranding] = useState<ConsoleBranding | null>(null);
  const [brandGeneration, setBrandGeneration] = useState(0);
  useEffect(() => {
    const bump = (): void => setBrandGeneration((n) => n + 1);
    window.addEventListener(BRANDING_CHANGED, bump);
    return (): void => window.removeEventListener(BRANDING_CHANGED, bump);
  }, []);
  useEffect(() => {
    let live = true;
    api
      .get<ConsoleBranding>('/api/tenant/console-branding')
      .then((data) => live && setBranding(data))
      .catch(() => live && setBranding(null));
    return (): void => {
      live = false;
    };
  }, [me.tenantId, brandGeneration]);
  useEffect(() => {
    const style = document.documentElement.style;
    applyConsoleBrand(style, branding, theme);
    // Signing out unmounts the shell; the sign-in screen is CosMon's.
    return (): void => applyConsoleBrand(style, null, theme);
  }, [branding, theme]);
  // The tab says whose console it is once the tenant has a look of its own.
  useEffect(() => {
    document.title = consoleTitle(branding);
    return (): void => {
      document.title = 'COSMON';
    };
  }, [branding]);

  const [paletteOpen, setPaletteOpen] = useState(false);
  const [newAssetOpen, setNewAssetOpen] = useState(false);

  /*
   * `/` opens the palette, and `g` then a letter goes to a destination — as in
   * every console an operator already knows, and never while they are typing
   * somewhere. The chord's decisions live in `shortcuts.ts`.
   */
  const chord = useRef<ChordState>({ armedAt: null });
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      const t = e.target as HTMLElement | null;
      const typing =
        t !== null &&
        (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT' || t.isContentEditable);
      const modified = e.metaKey || e.ctrlKey || e.altKey;
      // A dialog (the palette, a drawer) owns the keyboard while it is open.
      const inDialog = t !== null && t.closest('[role="dialog"]') !== null;

      if (e.key === '/' && !modified && !typing && !inDialog) {
        e.preventDefault();
        setPaletteOpen(true);
        return;
      }
      const { state, go } = readChord(chord.current, {
        key: e.key,
        at: Date.now(),
        modified,
        typing: typing || inDialog,
      });
      chord.current = state;
      if (go !== null) {
        e.preventDefault();
        navigate(routeTo(go));
      }
    };
    document.addEventListener('keydown', onKey);
    return (): void => document.removeEventListener('keydown', onKey);
  }, [navigate]);

  const paletteItems = useMemo<PaletteItem[]>(() => {
    const items: PaletteItem[] = [];
    for (const item of NAV) {
      const letter = Object.entries(GO_KEYS).find(([, v]) => v === item.view)?.[0];
      items.push({
        id: `view:${item.view}`,
        label: item.label,
        ...(letter !== undefined ? { shortcut: `g ${letter}` } : {}),
        group: 'Go to',
        icon: item.icon,
        onSelect: () => navigate(routeTo(item.view)),
      });
      for (const sub of SUB_VIEWS[item.view]) {
        if (sub === SUB_VIEWS[item.view][0]) continue;
        items.push({
          id: `view:${item.view}/${sub}`,
          label: `${item.label} › ${SUB_LABELS[sub] ?? sub}`,
          group: 'Go to',
          icon: item.icon,
          onSelect: () => navigate(routeTo(item.view, sub)),
        });
      }
    }
    if (fleet !== null) {
      /*
       * Acknowledge from anywhere. Only alerts nobody has yet: withdrawing an
       * acknowledgement is rarer and deserves the queue in front of it.
       */
      const assetLabel = new Map(fleet.assets.map((a) => [a.id, a.code ?? a.name]));
      for (const alert of fleet.alerts) {
        if (alert.ack_at !== null) continue;
        items.push({
          id: `ack:${alert.id}`,
          label: `Acknowledge ${assetLabel.get(alert.asset_id) ?? 'asset'} — ${alert.cause_label}`,
          hint: alert.severity,
          group: 'Alerts',
          icon: <Check size={16} />,
          onSelect: () => {
            api
              .post(`/api/alerts/${alert.id}/ack`)
              .then(reload)
              // Say nothing here and nobody would know it failed: land on the queue, which shows the truth.
              .catch(() => navigate(routeTo('alerts')));
          },
        });
      }
      /*
       * Sites, so a search for "COS-INFRA" offers the place as well as the
       * four hosts in it — and the place opens its overview.
       */
      const assetsAt = new Map<string, number>();
      for (const a of fleet.assets) if (a.site_id !== null) assetsAt.set(a.site_id, (assetsAt.get(a.site_id) ?? 0) + 1);
      for (const s of fleet.sites) {
        const n = assetsAt.get(s.id) ?? 0;
        items.push({
          id: `site:${s.id}`,
          label: s.code ?? s.name,
          hint: [s.code !== null && s.code !== s.name ? s.name : null, `${n} asset${n === 1 ? '' : 's'}`]
            .filter((x): x is string => x !== null)
            .join(' · '),
          group: 'Sites',
          icon: <MapPin size={16} />,
          onSelect: () => navigate({ view: 'fleet', sub: 'sites', assetId: null, siteId: s.id }),
        });
      }
      const siteName = new Map(fleet.sites.map((s) => [s.id, s.name]));
      for (const asset of fleet.assets) {
        items.push({
          id: `asset:${asset.id}`,
          label: asset.code ?? asset.name,
          hint: [
            asset.code !== null ? asset.name : null,
            // NX's name (070): so "C06" finds a camera linked under its CosMon name.
            asset.nx_name !== undefined && asset.nx_name !== null && asset.nx_name !== asset.name ? `NX: ${asset.nx_name}` : null,
            asset.site_id !== null ? siteName.get(asset.site_id) : null,
          ]
            .filter((x): x is string => typeof x === 'string' && x !== '')
            .join(' · '),
          group: 'Assets',
          onSelect: () => navigate({ view: 'fleet', sub: null, assetId: asset.id }),
        });
      }
    }
    return items;
  }, [fleet, navigate, reload]);

  const pageAction =
    view === 'fleet' ? (
      <Button variant="primary" size="sm" icon={<Plus size={14} />} onClick={() => setNewAssetOpen(true)}>
        New asset
      </Button>
    ) : null;

  return (
    <div className="app">
      <aside className="sidebar">
        <TenantSwitch
          me={me}
          branding={branding}
          onSwitched={refreshMe}
          onNavigateHome={() => navigate(routeTo('fleet'))}
        />

        <nav className="nav" aria-label="Main">
          {NAV.map((item) => (
            <button
              key={item.view}
              type="button"
              aria-current={view === item.view ? 'page' : undefined}
              className={item.ready ? undefined : 'pending'}
              onClick={() => navigate(routeTo(item.view))}
              title={item.ready ? undefined : `${item.label} — not ported yet`}
            >
              <span className="nav-icon" aria-hidden="true">
                {item.icon}
              </span>
              {item.label}
            </button>
          ))}
        </nav>

        <div className="sidebar-foot">
          <UserMenu
            email={me.user.email}
            name={me.user.display_name}
            theme={theme}
            onToggleTheme={onToggleTheme}
            onSignOut={async () => {
              await api.post('/api/auth/logout');
              await refreshMe();
            }}
          />
        </div>
      </aside>

      <div className="main">
        <header className="topbar">
          <h1>{VIEW_TITLES[view]}</h1>
          <span className="spacer" />
          <button type="button" className="topbar-search" onClick={() => setPaletteOpen(true)}>
            <Search size={14} aria-hidden="true" />
            <span>Search</span>
            <kbd>⌘K</kbd>
          </button>
          {pageAction}
        </header>

        {subs.length > 0 && (
          <div className="subbar">
            <Tabs
              label={`${VIEW_TITLES[view]} sections`}
              value={route.sub ?? subs[0] ?? ''}
              onChange={(sub) => navigate(routeTo(view, sub))}
              items={subs.map((sub) => ({
                value: sub,
                label: PENDING_SUBS.has(sub) ? (
                  <span className="pending">{SUB_LABELS[sub] ?? sub}</span>
                ) : (
                  SUB_LABELS[sub] ?? sub
                ),
              }))}
            />
          </div>
        )}

        <main className="content">
          {routeFailure !== null && (
            <Banner tone="warn" title={routeFailure.title}>
              {routeFailure.detail}
            </Banner>
          )}
          <Views
            route={route}
            fleet={fleet}
            fleetError={fleetError}
            onNavigate={navigate}
            reload={reload}
            isPlatformAdmin={me.user.is_platform_admin}
            isAdmin={me.tenant?.role === 'owner' || me.tenant?.role === 'admin'}
          />
        </main>
      </div>

      <CommandPalette open={paletteOpen} onOpenChange={setPaletteOpen} items={paletteItems} />
      <NewAsset
        open={newAssetOpen}
        onClose={() => setNewAssetOpen(false)}
        onCreated={() => {
          setNewAssetOpen(false);
          reload();
        }}
      />
    </div>
  );
}

/**
 * Which tenant you are in, and the way to change it.
 *
 * The first shell never showed the tenant once you were inside — email only —
 * which for a person serving several customers meant reading site names to
 * work out whose fleet was on screen. The name leads the rail now, and the
 * menu under it lists the others.
 */
function TenantSwitch({
  me,
  branding,
  onSwitched,
  onNavigateHome,
}: {
  me: Me;
  branding: ConsoleBranding | null;
  onSwitched: () => Promise<void>;
  onNavigateHome: () => void;
}): React.JSX.Element {
  const [tenants, setTenants] = useState<TenantOption[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (tenants !== null) return;
    try {
      const r = await api.get<{ tenants: TenantOption[] }>('/api/auth/tenants');
      setTenants(r.tenants);
    } catch {
      setTenants([]);
    }
  }, [tenants]);

  const enter = async (id: string): Promise<void> => {
    setError(null);
    try {
      await api.post('/api/auth/tenant', { tenantId: id });
      await onSwitched();
      onNavigateHome();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'could not switch tenant');
    }
  };

  const block = brandBlock(branding, me.tenant?.name ?? 'COSMON');
  const name = block.name;

  return (
    <div className={block.logoUrl !== null ? 'sidebar-brand with-logo' : 'sidebar-brand'}>
      <DropdownMenu.Root onOpenChange={(open) => open && void load()}>
        <DropdownMenu.Trigger asChild>
          <button
            type="button"
            className={block.logoUrl !== null ? 'tenant-switch has-logo' : 'tenant-switch'}
            aria-label={`Tenant: ${name}. Switch tenant`}
            title={name}
          >
            {block.logoUrl !== null ? (
              <span className={block.plated ? 'tenant-logo-plate plated' : 'tenant-logo-plate'}>
                <img className="tenant-logo" src={block.logoUrl} alt="" />
              </span>
            ) : (
              <span className="mark" aria-hidden="true" />
            )}
            <span className="tenant-name">{name}</span>
            <ChevronsUpDown size={14} className="tenant-switch-icon" aria-hidden="true" />
          </button>
        </DropdownMenu.Trigger>
        <DropdownMenu.Portal>
          <DropdownMenu.Content className="menu" align="start" sideOffset={4}>
            <DropdownMenu.Label className="menu-label">Tenants</DropdownMenu.Label>
            {tenants === null && <div className="menu-item muted">Loading…</div>}
            {tenants?.map((tenant) => (
              <DropdownMenu.Item
                key={tenant.id}
                className="menu-item"
                disabled={tenant.id === me.tenantId}
                onSelect={() => void enter(tenant.id)}
              >
                <Building size={14} aria-hidden="true" />
                <span className="menu-item-label">{tenant.name}</span>
                <span className="menu-item-hint">{tenant.role}</span>
                {tenant.id === me.tenantId && <Check size={14} aria-hidden="true" />}
              </DropdownMenu.Item>
            ))}
            {error !== null && <div className="menu-item fault">{error}</div>}
          </DropdownMenu.Content>
        </DropdownMenu.Portal>
      </DropdownMenu.Root>
    </div>
  );
}

function UserMenu({
  email,
  name,
  theme,
  onToggleTheme,
  onSignOut,
}: {
  email: string;
  name: string | null;
  theme: string;
  onToggleTheme: () => void;
  onSignOut: () => Promise<void>;
}): React.JSX.Element {
  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <button type="button" className="user-menu" title={email}>
          <span className="avatar" aria-hidden="true">
            {(name ?? email).slice(0, 1).toUpperCase()}
          </span>
          <span className="who">{name ?? email}</span>
          <ChevronsUpDown size={14} className="faint" aria-hidden="true" />
        </button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content className="menu" align="start" side="top" sideOffset={4}>
          <DropdownMenu.Label className="menu-label">{email}</DropdownMenu.Label>
          <DropdownMenu.Item className="menu-item" onSelect={onToggleTheme}>
            {theme === 'dark' ? <Sun size={14} aria-hidden="true" /> : <Moon size={14} aria-hidden="true" />}
            <span className="menu-item-label">{theme === 'dark' ? 'Light theme' : 'Dark theme'}</span>
          </DropdownMenu.Item>
          <DropdownMenu.Separator className="menu-sep" />
          <DropdownMenu.Item className="menu-item" onSelect={() => void onSignOut()}>
            <LogOut size={14} aria-hidden="true" />
            <span className="menu-item-label">Sign out</span>
          </DropdownMenu.Item>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}

function Views({
  route,
  fleet,
  fleetError,
  onNavigate,
  reload,
  isPlatformAdmin,
  isAdmin,
}: {
  route: Route;
  fleet: Fleet | null;
  fleetError: string | null;
  onNavigate: (next: Route) => void;
  reload: () => void;
  /* Passed down rather than re-fetched: `/api/me` is already in hand upstairs,
   * and a second fetch could disagree with the first for a moment. */
  isPlatformAdmin: boolean;
  /** Owner or admin of this tenant: decides whether to OFFER admin-only actions. The API is the control. */
  isAdmin: boolean;
}): React.JSX.Element {
  const { view, sub } = route;

  /*
   * Only the screens that read the fleet payload wait on it. The rest fetch
   * for themselves and must not be held behind a request they do not use.
   */
  const needsFleet = view === 'fleet' || view === 'alerts' || (view === 'settings' && sub === 'coverage');
  if (needsFleet && fleetError !== null) {
    return (
      <Banner tone="fault" title="Could not load the fleet">
        {fleetError}
      </Banner>
    );
  }
  if (needsFleet && fleet === null) return <Loading rows={6} cols={5} />;

  // Spread into fleet routes so a site's overview survives opening an asset over it.
  const site = route.siteId !== undefined ? { siteId: route.siteId } : {};

  switch (view) {
    case 'fleet':
      return (
        <>
          <FleetView
            fleet={fleet as Fleet}
            grouping={sub === 'sites' ? 'sites' : 'devices'}
            canExport={isAdmin}
            // The grouping — and the site, on a site's overview — travels with
            // the asset, so closing it returns here.
            onSelectAsset={(assetId) => onNavigate({ view: 'fleet', sub, assetId, ...site })}
            {...site}
            onOpenSite={(siteId) => onNavigate({ view: 'fleet', sub: 'sites', assetId: null, siteId })}
            onLeaveSite={() => onNavigate(routeTo('fleet', 'sites'))}
          />
          {route.assetId !== null && (
            <AssetDetailPanel
              assetId={route.assetId}
              /*
               * Whether this asset is one the grid behind the drawer is showing.
               *
               * The grid can only select from `fleet.assets`, so anything NOT in
               * that list arrived through the address bar — which is the case
               * where a 404 needs the tenant explanation rather than a bare
               * "not found". A deep link to a RETIRED asset also lands here,
               * since `/api/fleet` filters those out and `/detail` does not; it
               * resolves, so the distinction costs it nothing.
               */
              fromLink={!(fleet as Fleet).assets.some((a) => a.id === route.assetId)}
              onClose={() => onNavigate({ view: 'fleet', sub, assetId: null, ...site })}
              section={route.assetTab}
              onSectionChange={(assetTab) =>
                onNavigate({ view: 'fleet', sub, assetId: route.assetId, assetTab, ...site })
              }
            />
          )}
        </>
      );
    case 'alerts':
      return (
        <AlertsView
          alerts={(fleet as Fleet).alerts}
          fleet={fleet as Fleet}
          onChanged={reload}
          onOpenAsset={(assetId) => onNavigate({ view: 'fleet', sub: null, assetId })}
        />
      );
    case 'sites':
      return sub === 'map' ? <MapView /> : <SitesEditor canExport={isAdmin} />;
    case 'uptime':
      return sub === 'report' ? (
        <UptimeReportView canExport={isAdmin} />
      ) : sub === 'matrix' ? (
        <UptimeMatrixView />
      ) : (
        <UptimeView />
      );
    case 'footage':
      if (sub === 'pulls') return <PullsView />;
      if (sub === 'vision') return <NotPorted view="vision" />;
      return <Recordings />;
    case 'sim':
      return <SimView />;
    case 'activity':
      return <ActivityView />;
    case 'power':
      return <NotPorted view={sub === 'triage' ? 'triage' : 'solar'} />;
    case 'settings':
      switch (sub) {
        case 'channels':
          return <ChannelsView />;
        case 'routing':
          return <RoutingView />;
        case 'mail':
          // Both are about mail this tenant's people receive: who it comes
          // from, and when the scheduled ones arrive.
          return (
            <>
              <MailIdentityView />
              <ReportScheduleView />
            </>
          );
        case 'portal':
          return <PortalBrandingView />;
        case 'credentials':
          return <CredentialsEditor />;
        case 'security':
          // Both live on the Security screen because both are "how I get in":
          // splitting them would mean a person who wants to change their
          // password has to guess which of two settings screens owns it.
          return (
            <>
              <SecurityView />
              <PasswordForm />
            </>
          );
        case 'hosts':
          return <HostsView />;
        case 'remote':
          return <RemoteAccessView />;
        case 'coverage':
          return <StatusView fleet={fleet as Fleet} />;
        case 'platform':
          /*
           * The platform-admin gate is passed in rather than read here, because
           * the SCREEN is not the control — /api/platform/settings is. This only
           * decides whether to render an editor or the explanation of why there
           * isn't one, and a control that looks usable and 403s on save is worse
           * than an explanation.
           */
          return isPlatformAdmin ? (
            <>
              <ServiceStatus />
              <PlatformTenants />
              <PlatformRecorders />
              <SettingsView isPlatformAdmin />
            </>
          ) : (
            <SettingsView isPlatformAdmin={false} />
          );
        default:
          return <UsersView />;
      }
    default:
      return <NotPorted view={view} />;
  }
}

function StatusView({ fleet }: { fleet: Fleet }): React.JSX.Element {
  const byCheck = new Map<string, Record<string, number>>();
  for (const row of fleet.checkState) {
    const tally = byCheck.get(row.check_key) ?? {};
    tally[row.outcome] = (tally[row.outcome] ?? 0) + 1;
    byCheck.set(row.check_key, tally);
  }

  const stale = fleet.checkState.filter(
    (row) => row.last_definite === null && row.outcome !== 'unavailable',
  );

  return (
    <>
      <Section title="Checks, by outcome" flush>
        <table className="data dense">
          <thead>
            <tr>
              <th>Check</th>
              <th className="num">Pass</th>
              <th className="num">Fail</th>
              <th className="num">Unknown</th>
              <th className="num">Not checkable</th>
            </tr>
          </thead>
          <tbody>
            {[...byCheck.entries()].sort().map(([key, tally]) => (
              <tr key={key}>
                <td className="mono">{key}</td>
                <td className="num">{tally['pass'] ?? 0}</td>
                <td className={`num ${(tally['fail'] ?? 0) > 0 ? 'sev-critical' : ''}`}>
                  {tally['fail'] ?? 0}
                </td>
                <td className="num">{tally['unknown'] ?? 0}</td>
                <td className="num">{tally['unavailable'] ?? 0}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Section>

      <Section
        title="Never returned a definite answer"
        summary="A check that has never answered is a different problem from one that is failing."
      >
        {stale.length === 0 ? (
          <p className="muted small">Every check has produced at least one definite reading.</p>
        ) : (
          <ul className="small">
            {stale.map((row) => (
              <li key={`${row.asset_id}-${row.check_key}`}>
                <span className="mono">{row.check_key}</span> on{' '}
                {fleet.assets.find((a) => a.id === row.asset_id)?.code ?? row.asset_id}
              </li>
            ))}
          </ul>
        )}
      </Section>
    </>
  );
}

/**
 * Changing your own password.
 *
 * 🔴 This form is why `assertNoDevCredentials()` is satisfiable in practice and
 * not just in principle. The route existed for a day with nothing calling it,
 * which is the same "built and never connected" gap this product keeps finding
 * in its own history — a startup guard whose only remedy is a curl command is
 * still one that gets commented out.
 *
 * The current password is asked for because the server requires it: a session
 * alone must not be able to change the password and lock the owner out. The
 * form says so rather than making that look like bureaucracy.
 */
function PasswordForm(): React.JSX.Element {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{ tone: 'ok' | 'fault'; message: string } | null>(null);

  // Checked here as well as on the server, because the server cannot see it:
  // it receives one new password and has no idea what was typed twice.
  const mismatch = confirm !== '' && next !== confirm;
  const tooShort = next !== '' && next.length < 12;
  const canSubmit = current !== '' && next.length >= 12 && next === confirm && !busy;

  return (
    <Section
      title="Password"
      summary="Changing this signs out every other session on your account. This one stays open."
    >
      <div className="form-narrow">
        <Field id="pw-current" label="Current password" hint="Asked for so a stolen session cannot lock you out of your own account.">
          <input
            id="pw-current"
            type="password"
            autoComplete="current-password"
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
          />
        </Field>

        <Field
          id="pw-next"
          label="New password"
          hint="At least 12 characters. Length beats punctuation."
          error={tooShort ? 'Too short — 12 characters or more.' : null}
        >
          <input
            id="pw-next"
            type="password"
            autoComplete="new-password"
            value={next}
            onChange={(e) => setNext(e.target.value)}
          />
        </Field>

        <Field id="pw-confirm" label="New password again" error={mismatch ? 'These do not match.' : null}>
          <input
            id="pw-confirm"
            type="password"
            autoComplete="new-password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
          />
        </Field>

        <Button
          variant="primary"
          disabled={!canSubmit}
          busy={busy}
          onClick={async () => {
            setBusy(true);
            setResult(null);
            try {
              const r = await api.post<{ otherSessionsEnded: number }>('/api/auth/password', {
                current,
                next,
              });
              setCurrent('');
              setNext('');
              setConfirm('');
              // The session count is reported, not swallowed. "Signed out 3 other
              // sessions" is the evidence that the change did the thing somebody
              // changes a password FOR.
              setResult({
                tone: 'ok',
                message:
                  r.otherSessionsEnded === 0
                    ? 'Password changed. No other sessions were open.'
                    : `Password changed, and ${r.otherSessionsEnded} other session(s) were signed out.`,
              });
            } catch (err) {
              setResult({
                tone: 'fault',
                message: err instanceof ApiError ? err.message : 'could not change the password',
              });
            } finally {
              setBusy(false);
            }
          }}
        >
          Change password
        </Button>

        {result !== null && (
          <Toast message={result.message} tone={result.tone} onDismiss={() => setResult(null)} />
        )}
      </div>
    </Section>
  );
}

function SecurityView(): React.JSX.Element {
  const [status, setStatus] = useState<MfaStatus | null>(null);
  /** Remounts the policy panel after enrolment, so its "you have MFA" gate re-reads. */
  const [generation, setGeneration] = useState(0);

  const load = useCallback(async () => {
    setStatus(await api.get<MfaStatus>('/api/auth/mfa/status'));
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <>
      <Section
        title="Two-factor authentication"
        summary={
          status === null
            ? undefined
            : `${status.enrolled ? 'Enrolled.' : 'Not enrolled.'} ${
                status.required ? `Required — ${status.reasons.join('; ')}.` : 'Not currently required for this tenant.'
              }${status.enrolled ? ` ${status.recoveryCodesRemaining} recovery codes remaining.` : ''}`
        }
      >
        <div className="form-narrow">
          <MfaEnrol
            enrolled={status?.enrolled === true}
            onEnrolled={() => {
              void load();
              setGeneration((n) => n + 1);
            }}
          />
        </div>
      </Section>
      <TenantMfaPolicy key={generation} />
    </>
  );
}

/**
 * What each unported screen is actually waiting on.
 *
 * 🔴 Identical dashed boxes imply a uniform stall, and the stall is not
 * uniform: two of these are waiting on a driver that does not exist and one on
 * a decision. Saying so on the screen is the difference between a roadmap and
 * a shrug.
 */
const BLOCKERS: Record<string, { blocker: string; detail: string }> = {
  vision: {
    blocker: 'Waiting on the snapshot pipeline being ported.',
    detail:
      '15,551 snapshots already carry a detected issue and no alert has ever been raised from ' +
      'one. The gap is the pipeline, not this screen.',
  },
  solar: {
    blocker: 'Waiting on a solar-controller driver.',
    detail:
      'Capability keys, then check kinds, then a push-only driver and a TCP listener bound to a ' +
      'specific address. New device coverage, not a missing view.',
  },
  triage: {
    blocker: 'Waiting on the MPPT frame identity decision.',
    detail:
      'Building the listener first would bake the wrong key into a UNIQUE constraint, and a ' +
      'routable address is the wrong answer.',
  },
};

function NotPorted({ view }: { view: string }): React.JSX.Element {
  const known = BLOCKERS[view];
  return (
    <Empty
      title={`${SUB_LABELS[view] ?? VIEW_TITLES[view as ViewKey] ?? view} has not been ported yet`}
      body={known === undefined ? 'Listed rather than hidden, so the gap stays visible.' : `${known.blocker} ${known.detail}`}
    />
  );
}

function SignIn({
  onSignedIn,
  theme,
  onToggleTheme,
}: {
  onSignedIn: () => Promise<void>;
  theme: string;
  onToggleTheme: () => void;
}): React.JSX.Element {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [needsMfa, setNeedsMfa] = useState(false);
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);

  return (
    <div className="centre">
      <div className="card">
        <div className="row centre-head">
          <span className="brand-inline">
            <span className="mark" aria-hidden="true" />
            <span className="name">COSMON</span>
          </span>
          <span className="spacer" />
          <Button
            variant="ghost"
            size="sm"
            iconOnly
            icon={theme === 'dark' ? <Sun size={16} /> : <Moon size={16} />}
            onClick={onToggleTheme}
          >
            {theme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}
          </Button>
        </div>

        {!needsMfa ? (
          <form
            onSubmit={async (e) => {
              e.preventDefault();
              setError(null);
              try {
                await api.post('/api/auth/login', { email, password });
                // Whether MFA is REQUIRED is answered at tenant entry, so this
                // only asks for a code when the account already has one.
                const status = await api.get<MfaStatus>('/api/auth/mfa/status');
                if (status.enrolled && !status.satisfiedForSession) setNeedsMfa(true);
                else await onSignedIn();
              } catch (err) {
                setError(err instanceof ApiError ? err.message : 'could not sign in');
              }
            }}
          >
            <Field id="email" label="Email or username">
              {/* 🚩 `type="text"`, not `type="email"`, so the local `test`
              account can sign in at all — the browser rejects a value with
              no @ before the request is ever made. The server never
              validated the format (it matches on `lower(email)`), so this
              only relaxes the client. Put it back when the convenience
              account goes; see SEED_PASSWORD in db/fixtures.ts. */}
              <input
                id="email"
                type="text"
                autoComplete="username"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </Field>
            <Field id="password" label="Password">
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </Field>
            <Button variant="primary" type="submit">
              Sign in
            </Button>
          </form>
        ) : (
          <form
            onSubmit={async (e) => {
              e.preventDefault();
              setError(null);
              try {
                await api.post('/api/auth/mfa', { code });
                await onSignedIn();
              } catch (err) {
                setError(err instanceof ApiError ? err.message : 'invalid code');
              }
            }}
          >
            <Field id="code" label="Code from your authenticator, or a recovery code">
              <input id="code" value={code} onChange={(e) => setCode(e.target.value)} autoFocus />
            </Field>
            <Button variant="primary" type="submit">
              Verify
            </Button>
          </form>
        )}

        {error !== null && <Banner tone="fault">{error}</Banner>}
      </div>
    </div>
  );
}

/**
 * Choosing a tenant.
 *
 * A separate step because a user may belong to several, and because MFA is
 * enforced HERE rather than at password time — a tenant that requires a second
 * factor refuses entry, and says which tenant is asking.
 *
 * The list comes from `own_memberships` (migration 008). Before that policy
 * existed this screen could not be written honestly: listing a user's tenants
 * is a cross-tenant read at a moment when no tenant context exists, so it
 * returned nothing and the first version of this asked people to type a UUID.
 */
function TenantPicker({
  onEntered,
  onSignOut,
}: {
  onEntered: () => Promise<void>;
  onSignOut: () => Promise<void>;
}): React.JSX.Element {
  const [tenants, setTenants] = useState<TenantOption[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reasons, setReasons] = useState<string[]>([]);
  /**
   * The tenant that refused entry for want of a second factor this person has
   * not set up. Enrolment is offered here rather than sending them to a
   * Security screen they cannot reach without entering a tenant first.
   */
  const [needsEnrolment, setNeedsEnrolment] = useState<TenantOption | null>(null);
  /**
   * The tenant that refused entry because this session has not used the
   * authenticator the account already has — a session older than its
   * enrolment. Asked for here, since the sign-in screen asks only at password
   * time (see tenant-entry.ts).
   */
  const [needsCode, setNeedsCode] = useState<TenantOption | null>(null);
  const [code, setCode] = useState('');

  useEffect(() => {
    api
      .get<{ tenants: TenantOption[] }>('/api/auth/tenants')
      .then((r) => setTenants(r.tenants))
      .catch(() => setTenants([]));
  }, []);

  async function enter(id: string): Promise<void> {
    setError(null);
    setReasons([]);
    try {
      await api.post('/api/auth/tenant', { tenantId: id });
      setNeedsEnrolment(null);
      setNeedsCode(null);
      await onEntered();
    } catch (err) {
      if (err instanceof ApiError) {
        const body = err.body as { reasons?: string[] } | null;
        if (err.status === 403 && (body?.reasons?.length ?? 0) > 0) {
          const status = await api.get<MfaStatus>('/api/auth/mfa/status').catch(() => null);
          const step = mfaRefusalStep(status);
          if (step === 'enrol') {
            setNeedsEnrolment(tenants?.find((t) => t.id === id) ?? null);
            setReasons(body?.reasons ?? []);
            return;
          }
          if (step === 'code') {
            setNeedsCode(tenants?.find((t) => t.id === id) ?? null);
            setReasons(body?.reasons ?? []);
            return;
          }
        }
        setError(err.message);
        setReasons(body?.reasons ?? []);
      }
    }
  }

  if (needsEnrolment !== null) {
    return (
      <div className="centre">
        <div className="card">
          <h2 className="section-title">Set up two-factor authentication</h2>
          <p className="small muted invite-lede">
            {needsEnrolment.name} requires it ({reasons.join('; ')}). It takes a minute with an authenticator
            app on your phone.
          </p>
          <MfaEnrol
            enrolled={false}
            continueLabel={`Continue to ${needsEnrolment.name}`}
            onEnrolled={() => void enter(needsEnrolment.id)}
          />
          <div className="row picker-back">
            <Button variant="ghost" size="sm" onClick={() => setNeedsEnrolment(null)}>
              Back to tenants
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (needsCode !== null) {
    return (
      <div className="centre">
        <div className="card">
          <h2 className="section-title">Enter your authenticator code</h2>
          <p className="small muted invite-lede">
            {needsCode.name} requires two-factor authentication ({reasons.join('; ')}).
          </p>
          <form
            onSubmit={async (e) => {
              e.preventDefault();
              setError(null);
              try {
                await api.post('/api/auth/mfa', { code });
                setCode('');
                await enter(needsCode.id);
              } catch (err) {
                setError(err instanceof ApiError ? err.message : 'invalid code');
              }
            }}
          >
            <Field id="picker-code" label="Code from your authenticator, or a recovery code">
              <input
                id="picker-code"
                inputMode="numeric"
                autoComplete="one-time-code"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                autoFocus
              />
            </Field>
            <Button variant="primary" type="submit" disabled={code.trim() === ''}>
              Verify
            </Button>
          </form>
          {error !== null && <Banner tone="fault">{error}</Banner>}
          <p className="small muted">Lost your device? Ask an administrator to re-enrol you.</p>
          <div className="row picker-back">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setNeedsCode(null);
                setError(null);
                setReasons([]);
              }}
            >
              Back to tenants
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="centre">
      <div className="card">
        <h2 className="section-title">Choose a tenant</h2>
        <p className="small muted">One identity, one password, however many customers you serve.</p>

        {tenants === null && <Loading rows={2} cols={1} />}

        {tenants !== null && tenants.length === 0 && (
          <Empty
            title="Not a member of any active tenant"
            body="An administrator has to add you."
          />
        )}

        <div className="stack">
          {(tenants ?? []).map((tenant) => (
            <button key={tenant.id} type="button" className="tenant-choice" onClick={() => void enter(tenant.id)}>
              <strong>{tenant.name}</strong>
              <span className="small muted">
                {' '}
                · {tenant.role}
                {tenant.require_mfa && ' · requires 2FA'}
              </span>
            </button>
          ))}
        </div>

        {error !== null && <Banner tone="fault">{error}</Banner>}
        {reasons.length > 0 && (
          <p className="small muted">
            {reasons.join('; ')}. Sign out and sign in again with your authenticator code, or ask an
            administrator to re-enrol you if you have lost it.
          </p>
        )}
        {/* Never a dead end: this screen comes before the shell and its user menu. */}
        <div className="row picker-back">
          <Button variant="ghost" size="sm" onClick={() => void onSignOut()}>
            Sign out
          </Button>
        </div>
      </div>
    </div>
  );
}
